import type { ru } from "./ru";

// Machine translation. Native English speaker should proofread before launch.
// TODO(translation-review): EN proofread.
export const en: typeof ru = {
  brand: {
    name: "Orda OS",
    tagline: "The operating system for cafés",
  },
  nav: {
    apps: "Apps",
    features: "Features",
    caseStudy: "Case study",
    pricing: "Pricing",
    faq: "FAQ",
    deck: "Deck",
    cta: "Get started",
    ctaShort: "Start",
  },
  rail: {
    ariaLabel: "Section navigation",
  },
  hero: {
    eyebrow: "The operating system for cafés · Kazakhstan",
    nameMeaning: "Orda OS is your venue's single control center: menu, kitchen, POS, delivery and inventory — all in one place. One platform instead of a dozen tools.",
    serifLine: "menu, kitchen, POS and inventory — one organism",
    scrollHint: "Scroll to step inside",
    subtitle:
      "Guests scan a QR code and order on their own. The kitchen sees the order instantly. POS, inventory, analytics and AI — one platform. No installs, live in one day.",
    ctaDemo: "Live demo",
    ctaContact: "Connect your café",
    status: "Onboarding cafés — live in 1 day",
    glass: {
      platform: { label: "Platform", value: "10 apps in one" },
      launch: { label: "Launch", value: "Live in 1 day" },
      langs: { label: "Languages", value: "Қазақша · Русский · English" },
    },
    stats: {
      apps: "apps",
      features: "features",
      langs: "languages",
    },
    ticker: [
      "QR menu",
      "AR dishes in 3D",
      "Realtime kitchen",
      "POS & Kaspi QR",
      "AI assistant",
      "Inventory & purchasing",
      "Reservations",
      "Delivery",
      "Analytics",
      "Қазақша · Русский · English",
    ],
  },
  apps: {
    label: "Apps",
    title: "One platform,",
    accent: "ten apps",
    aside:
      "Everyone in the café gets their own screen: guest, cook, cashier, waiter, owner. All connected in realtime — a guest's order reaches the kitchen before they put the phone down.",
    dragHint: "drag to rotate",
    openDemo: "Open demo",
    downloadCashier: "Download POS for Windows",
    totalNote:
      "And that's not all: purchaser, receiver, storekeeper and courier — {total} apps in the platform.",
    items: {
      guest: {
        name: "Guest menu",
        role: "scan, choose, order",
        desc:
          "A QR code on the table opens a menu with photos, 3D dishes in AR and an AI advisor. Shared table cart, payment from the phone — no waiting for a waiter.",
        tags: ["QR", "AR / 3D", "AI advisor", "Shared cart"],
      },
      kitchen: {
        name: "Kitchen",
        role: "sees orders instantly",
        desc:
          "The cook's screen: new orders arrive in realtime, one tap — “cooking”, another — “ready”. The waiter gets notified immediately.",
        tags: ["KDS", "Realtime", "Dish statuses"],
      },
      cashier: {
        name: "POS",
        role: "shifts, payments, receipts",
        desc:
          "Web POS with shifts, discounts and split payments. Kaspi QR, cash, card — close a table in three taps.",
        tags: ["Kaspi QR", "Split payment", "Shifts"],
      },
      waiter: {
        name: "Waiter",
        role: "tables in your pocket",
        desc:
          "A PWA on the waiter's phone: their tables, guest calls, orders and kitchen statuses. Push notification when a dish is ready.",
        tags: ["PWA", "Push", "Guest call"],
      },
      admin: {
        name: "Admin panel",
        role: "numbers, not gut feelings",
        desc:
          "Menu, prices, stop list, staff, reviews and analytics: revenue, top dishes, average check, guest funnel — on one screen.",
        tags: ["Analytics", "Menu", "Staff"],
      },
      booking: {
        name: "Reservations",
        role: "a table in advance",
        desc:
          "Guests book a table with a Kaspi prepayment — fake bookings disappear. Floor map: booths, karaoke, billiards.",
        tags: ["Prepayment", "Floor map"],
      },
    },
  },
  features: {
    label: "Features",
    title: "What the platform",
    accent: "can do",
    aside:
      "140+ features already in the product. Here are the eight that make cafés switch to Orda OS.",
    list: [
      {
        title: "QR menu & AR",
        sub: "guest experience",
        body:
          "A photo menu in three languages opens in a second, no app required. Flagship dishes have 3D models: guests point the camera and place a virtual plate on their table. A menu like this sells by itself.",
        tags: ["3 languages", "AR", "No app"],
      },
      {
        title: "Realtime kitchen",
        sub: "zero lost orders",
        body:
          "A guest's order appears on the kitchen screen instantly — no paper, no shouting. Race-safe architecture: two cooks will never grab the same dish twice, statuses converge on any connection.",
        tags: ["KDS", "Realtime"],
      },
      {
        title: "POS & payments",
        sub: "Kaspi QR · split · shifts",
        body:
          "Guests pay from their own phone via Kaspi QR, groups split the bill. The cashier sees shifts, discounts, refunds. Receipts print to an ESC/POS printer.",
        tags: ["Kaspi QR", "Split payment"],
      },
      {
        title: "Inventory & purchasing",
        sub: "purchaser → receiver → storekeeper",
        body:
          "Purchases with receipt photos straight from the market, receiving with discrepancy tracking, stock and write-offs. Three roles — three separate apps, the owner sees the whole chain.",
        tags: ["Receipt photos", "Discrepancies"],
      },
      {
        title: "AI assistants",
        sub: "for guest, cook and owner",
        body:
          "For guests, AI builds a dinner within a “5,000 ₸ for two” budget. It helps the cook through the shift. It answers the owner's “how much did we make on Friday?” — in Kazakh and Russian.",
        tags: ["GPT", "KZ / RU / EN"],
      },
      {
        title: "Telegram alerts",
        sub: "the café in the owner's pocket",
        body:
          "New order, guest left without paying, receiving discrepancy, daily report — the owner gets everything in Telegram the moment it happens. No admin panel needed.",
        tags: ["Owner alerts", "Daily report"],
      },
      {
        title: "Loyalty & reviews",
        sub: "guests come back",
        body:
          "Points for visits, returning-guest recognition, photo reviews after payment and a wheel of fortune. A returning guest is the cheapest marketing there is.",
        tags: ["Points", "Wheel of fortune"],
      },
      {
        title: "Delivery & courier",
        sub: "beyond the dining room",
        body:
          "Guests order home delivery from the same storefront; the courier runs the order in their own app with status tracking and an SOS button.",
        tags: ["Delivery", "Tracking"],
      },
    ],
  },
  process: {
    label: "Launch",
    title: "Live in",
    accent: "one day",
    aside:
      "Onboarding shouldn't feel like a renovation. Here's the whole journey — from the first call to the first QR orders.",
    steps: [
      {
        when: "Day 1 · morning",
        title: "We set up your café",
        body:
          "We create the tenant, load the menu (a photo of your paper menu works), prices, dish photos and tables. We do everything — you only bring the menu.",
      },
      {
        when: "Day 1 · afternoon",
        title: "QR on the tables",
        body:
          "We print a QR code for every table. The token rotates after every payment — it can't be forged or bookmarked.",
      },
      {
        when: "Day 1 · evening",
        title: "We train the team",
        body:
          "30 minutes with the waiters and the kitchen. PIN login, no passwords, no 40-page manuals. By evening, guests are already ordering on their own.",
      },
      {
        when: "After that",
        title: "We stay with you",
        body:
          "Telegram alerts, zero-downtime updates, support in Kazakh and Russian. The platform grows — you never reinstall anything.",
      },
    ],
    footnote:
      "No hardware to buy: your staff's phones and any tablet for the kitchen are enough.",
  },
  caseAlem: {
    label: "Case study",
    title: "Running",
    accent: "in a real café",
    quote:
      "The first café on the platform: QR menus on the tables, the kitchen on a tablet, POS and inventory — all live, not a demo stand.",
    location: "First client of the platform · live since May 2026",
    liveBadge: "Live café",
    stats: {
      dishes: "dishes on the menu",
      tables: "tables with QR",
      staff: "staff members",
      ar: "dishes in AR",
    },
    cta: "Open Älem's menu",
    ctaNote: "This is the real storefront — the same menu guests see at the tables.",
  },
  pricing: {
    label: "Pricing",
    title: "Honest",
    accent: "pricing",
    subtitle:
      "No long-term contracts — cancel any month. Discounted launch pricing, in tenge (₸).",
    popular: "Popular",
    perMonth: "/mo",
    promo: "Launch",
    contactUs: "Let's talk",
    freeTrial: "15 days free",
    validUntil: "Discounted prices are valid only until the end of 2026",
    starter: {
      name: "Starter",
      price: "10 000 ₸",
      oldPrice: "16 000 ₸",
      currency: "",
      features: [
        "Up to 10 tables",
        "Guest QR menu",
        "One admin",
        "Basic analytics",
        "Email support",
      ],
      cta: "Get started",
    },
    delivery: {
      name: "Delivery",
      price: "15 000 ₸",
      oldPrice: "23 000 ₸",
      currency: "",
      features: [
        "Storefront for delivery",
        "Order home from the same menu",
        "Courier app with tracking",
        "Kitchen (KDS)",
        "POS + Kaspi QR",
        "Chat support",
      ],
      cta: "Get started",
    },
    pro: {
      name: "Pro",
      price: "25 000 ₸",
      oldPrice: "39 000 ₸",
      currency: "",
      features: [
        "Up to 50 tables",
        "Everything in Starter",
        "Waiter PWA",
        "Kitchen (KDS)",
        "POS + Kaspi QR",
        "AI assistant",
        "Table reservations",
        "Chat support",
      ],
      cta: "Get started",
    },
    enterprise: {
      name: "Enterprise",
      price: "40 000 ₸",
      oldPrice: "62 000 ₸",
      currency: "",
      features: [
        "Unlimited tables",
        "Multi-branch (chains)",
        "Custom integrations",
        "White-label branding",
        "SLA + priority support",
        "Dedicated manager",
      ],
      cta: "Let's talk",
    },
    network: {
      name: "Network",
      price: "On request",
      oldPrice: "",
      currency: "",
      features: [
        "Multiple venues in one dashboard",
        "Revenue rollup across the chain",
        "White-label for every venue",
        "Monthly billing per venue",
        "Custom integrations and onboarding",
        "Dedicated manager",
      ],
      cta: "Contact us",
    },
  },
  builder: {
    label: "Builder",
    title: "Build",
    accent: "your own plan",
    subtitle:
      "Like a mobile operator: a 2,000 ₸ base — then move the sliders. Pay only for what you actually use.",
    presets: "Ready-made bundles",
    presetTakeaway: "Takeaway spot",
    presetCafe: "Café with tables",
    presetResto: "Restaurant",
    presetMax: "All inclusive",
    base: "Platform base",
    baseHint: "QR menu storefront, POS (web + Windows), admin panel, stock tracking, takeaway & pickup",
    hall: "Dining hall & tables",
    hallHint: "QR on tables, ordering from the guest's phone, waiter app",
    hallOptions: ["No hall", "Up to 10 tables", "Up to 30 tables", "Up to 50 tables", "Unlimited"],
    staff: "Staff",
    staffHint: "Staff accounts: cashiers, waiters, cooks…",
    staffOptions: ["Up to 5", "Up to 10", "Up to 20", "Unlimited"],
    modules: "Modules",
    mDelivery: "Delivery",
    mDeliveryHint: "Delivery storefront, couriers on a live map, guest chat",
    mAi: "AI assistant",
    mAiHint: "Chat assistant for guests and staff",
    mWl: "Your own brand",
    mWlHint: "Your café's logo and name instead of Orda OS (white label)",
    included: "Included in every build",
    includedList: ["Kitchen (KDS)", "Table booking", "Loyalty", "Warehouse & purchasing", "Accountant cabinet", "Analytics", "6 menu languages", "Kaspi QR"],
    total: "Your plan",
    perMonth: "/mo",
    matches: "= plan",
    free: "incl.",
    cta: "Get a quote",
    ctaHint: "Your configuration goes to the manager with the request — we'll call back, confirm the price and set you up.",
  },
  faq: {
    label: "FAQ",
    title: "Common",
    accent: "questions",
    items: [
      {
        q: "How long does onboarding take?",
        a: "1 day. We create your tenant, load the menu (a photo works), print QR codes for the tables and train the staff in 30 minutes. Guests start ordering via QR on the very first evening.",
      },
      {
        q: "What about my café's data?",
        a: "Data lives on Supabase (PostgreSQL + RLS isolation). Every café is isolated — your cook only sees your orders. Full CSV export available on request.",
      },
      {
        q: "Does it fit a small café with 4 tables?",
        a: "Yes. The Starter plan covers up to 10 tables. Small cafés often see the biggest effect — one waiter, and the QR menu takes half the work off their hands.",
      },
      {
        q: "Do I need a fiscal cash register (Kazakhstan)?",
        a: "Orda OS handles orders and payment via Kaspi QR. Fiscalization (online cash register) is a separate integration — we'll add it for you if you use Salyk/an online register.",
      },
      {
        q: "What if the internet goes down?",
        a: "The platform only needs an ordinary connection — even mobile data works. Data lives in the cloud, nothing gets lost and it's reachable from any device. For venues with unstable connectivity we tailor a solution to your setup.",
      },
    ],
  },
  lead: {
    label: "Request",
    title: "Got a café?",
    subtitle:
      "Leave your number — I'll call back within an hour, show the platform on your menu and price a plan for your format.",
    nameLabel: "Your name",
    namePlaceholder: "Almas",
    phoneLabel: "Phone",
    cafeLabel: "Café name",
    cafePlaceholder: "Café Älem",
    cityLabel: "City",
    noteLabel: "Message",
    notePlaceholder: "How many tables, what matters, which integrations you need...",
    submit: "Get an offer",
    submitting: "Sending...",
    privacy:
      "By submitting, you agree to data processing for follow-up. No spam, ever.",
    successTitle: "Thank you!",
    successBody:
      "Your request is in. We'll call back within an hour — looking forward to meeting you 🤝",
    configAttached: "Your builder configuration is attached to the request",
    errors: {
      name_too_short: "Name is too short (minimum 2 characters).",
      name_too_long: "Name is too long (maximum 80 characters).",
      phone_invalid: "Phone looks invalid. At least 8 digits.",
      note_too_long: "Message is too long (maximum 1000 characters).",
      rate_limited: "Too many requests from this IP. Try again in an hour or message us on Telegram.",
      DB_ERROR: "Couldn't save the request. Try again or message us on Telegram.",
      INVALID_JSON: "Data submission error. Refresh the page.",
      unknown: "Something went wrong. Try again or message us on Telegram.",
    },
  },
  contact: {
    title: "Contact",
    subtitle: "Write to us — we reply within an hour.",
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    instagram: "Instagram",
    email: "Email",
    message: "Hello! I'm interested in Orda OS for my café.",
  },
  waiter: {
    eyebrow: "Guest · digital host",
    title: "Your digital",
    accent: "waiter",
    body:
      "An interactive 3D host greets every guest, suggests dishes by budget and mood, and guides them straight to ordering — in three languages, with no queue and no waiting.",
    chips: ["Follows your cursor", "Real-time 3D", "AI advisor"],
    cta: "Live demo",
    hint: "Move your cursor — it follows you",
  },
  band: {
    eyebrow: "Orda OS",
    line: "Menu, kitchen, POS and inventory — one organism.",
    sub: "One platform instead of ten services. Live in one day.",
    cta: "Connect your café",
  },
  footer: {
    copyright: "© 2026 Orda OS · e-menu.bar",
    tagline: "Made in Kazakhstan 🇰🇿",
    privacy: "Privacy",
    terms: "Terms",
    download: "Windows POS",
  },
  legal: {
    back: "← Back home",
  },
};
